import React, { useState, useEffect, useRef } from 'react';
import { useEmpresaAuth } from '../../../contexts/EmpresaAuthContext';
import Layout from '../../../components/shared/Layout/Layout';
import Button from '../../../components/shared/Button/Button';
import {
  uploadEmpresaLogo,
  deleteEmpresaLogo,
  uploadWithResizePrompt,
  resizeConfirmOptions,
} from '../../../services/uploads';
import { useConfirm } from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import { mergeDefaults } from '../../../utils/mergeDefaults';
import {
  SettingsContainer,
  SettingsHeader,
  SettingsCard,
  SettingsSection,
  DangerZone,
  HoursRow,
  HoursDay,
  HoursTimes,
  LogoRow,
  LogoPreview,
  LogoActions,
  LogoButtons
} from './Settings.styles';
import {
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FormTextarea,
  SwitchGroup,
  Switch,
  FormGrid
} from '../../../components/shared/Form/Form.styles';

const DEFAULT_BUSINESS_INFO = {
  businessName: '',
  contactName: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  businessType: '',
  description: ''
};

const DEFAULT_NOTIFICATIONS = {
  emailReservations: true,
  emailPayments: true,
  smsReservations: false,
  pushNotifications: true
};

const DEFAULT_OPERATING_HOURS = {
  monday: { open: '09:00', close: '18:00', closed: false },
  tuesday: { open: '09:00', close: '18:00', closed: false },
  wednesday: { open: '09:00', close: '18:00', closed: false },
  thursday: { open: '09:00', close: '18:00', closed: false },
  friday: { open: '09:00', close: '18:00', closed: false },
  saturday: { open: '10:00', close: '20:00', closed: false },
  sunday: { open: '10:00', close: '16:00', closed: false }
};

const DEFAULT_PAYMENT_SETTINGS = {
  bankAccount: '',
  clabe: '',
  commissionRate: 5.0,
  autoRelease: true,
  releaseDelay: 24
};

const Settings = () => {
  const { empresa, logout } = useEmpresaAuth();
  const { confirm, dialog } = useConfirm();
  const [businessInfo, setBusinessInfo] = useState(DEFAULT_BUSINESS_INFO);
  const [notifications, setNotifications] = useState(DEFAULT_NOTIFICATIONS);
  const [operatingHours, setOperatingHours] = useState(DEFAULT_OPERATING_HOURS);
  const [paymentSettings, setPaymentSettings] = useState(DEFAULT_PAYMENT_SETTINGS);

  // El logo vive en su propio estado: subirlo o quitarlo no debe pisar lo que
  // se esté editando en el formulario de información del negocio.
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState('');
  const logoInput = useRef(null);

  useEffect(() => {
    if (empresa) {
      setLogoUrl((current) => current || empresa.logoUrl || null);
      setBusinessInfo(mergeDefaults(DEFAULT_BUSINESS_INFO, {
        businessName: empresa.businessName,
        contactName: empresa.name,
        email: empresa.email,
        phone: empresa.phone,
        address: empresa.address,
        city: empresa.city,
        state: empresa.state,
        businessType: empresa.businessType,
        description: empresa.description
      }));
    }
  }, [empresa]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetch('/api/empresa/settings', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('empresa-token')}`
          }
        });
        const data = await response.json();
        
        if (data.success) {
          setLogoUrl(data.businessInfo?.logoUrl || null);
          // La API puede omitir bloques enteros o traer campos null: se mezcla
          // sobre los valores por defecto (mismo patrón que mergeAvailability).
          setBusinessInfo(mergeDefaults(DEFAULT_BUSINESS_INFO, data.businessInfo));
          setNotifications(mergeDefaults(DEFAULT_NOTIFICATIONS, data.notifications));
          setOperatingHours(mergeDefaults(DEFAULT_OPERATING_HOURS, data.operatingHours));
          setPaymentSettings(mergeDefaults(DEFAULT_PAYMENT_SETTINGS, data.paymentSettings));
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    
    if (empresa) {
      loadSettings();
    }
  }, [empresa]);

  // Mantiene el logo en el empresa guardado en localStorage (sin tocar el
  // contexto, que reiniciaría el formulario) para que una recarga lo conserve.
  const rememberLogo = (url) => {
    try {
      const stored = JSON.parse(localStorage.getItem('empresa') || 'null');
      if (stored) {
        localStorage.setItem('empresa', JSON.stringify({ ...stored, logoUrl: url }));
      }
    } catch (e) {
      // sin localStorage no pasa nada: el logo se vuelve a leer de la API
    }
  };

  const handleLogoSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;

    setLogoError('');
    setLogoBusy(true);
    try {
      const url = await uploadWithResizePrompt(
        uploadEmpresaLogo,
        file,
        (name) => confirm(resizeConfirmOptions(name))
      );
      if (url) {
        setLogoUrl(url);
        rememberLogo(url);
      }
    } catch (error) {
      setLogoError(error.message);
    } finally {
      setLogoBusy(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!window.confirm('¿Quitar el logo de tu empresa?')) return;
    setLogoError('');
    setLogoBusy(true);
    try {
      await deleteEmpresaLogo();
      setLogoUrl(null);
      rememberLogo(null);
    } catch (error) {
      setLogoError(error.message);
    } finally {
      setLogoBusy(false);
    }
  };

  const handleBusinessInfoChange = (e) => {
    setBusinessInfo({
      ...businessInfo,
      [e.target.name]: e.target.value
    });
  };

  const handleNotificationChange = (key) => {
    setNotifications({
      ...notifications,
      [key]: !notifications[key]
    });
  };

  const handleOperatingHourChange = (day, field, value) => {
    setOperatingHours({
      ...operatingHours,
      [day]: {
        ...operatingHours[day],
        [field]: value
      }
    });
  };

  const handlePaymentSettingsChange = (e) => {
    setPaymentSettings({
      ...paymentSettings,
      [e.target.name]: e.target.value
    });
  };

  const handleSaveBusinessInfo = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/empresa/settings/business-info', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('empresa-token')}`
        },
        body: JSON.stringify(businessInfo)
      });
      const result = await response.json();

      if (result.success) {
        alert('Información de negocio actualizada');
      } else {
        alert(result.error || 'Error al actualizar');
      }
    } catch (error) {
      alert('Error de conexión');
    }
  };

  const handleSaveNotifications = async () => {
    try {
      const response = await fetch('/api/empresa/settings/notifications', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('empresa-token')}`
        },
        body: JSON.stringify(notifications)
      });
      const result = await response.json();
      if (result.success) {
        alert('Preferencias de notificaciones actualizadas');
      } else {
        alert(result.error || 'Error al actualizar');
      }
    } catch (error) {
      alert('Error de conexión');
    }
  };

  const handleSaveOperatingHours = async () => {
    try {
      const response = await fetch('/api/empresa/settings/operating-hours', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('empresa-token')}`
        },
        body: JSON.stringify(operatingHours)
      });
      const result = await response.json();
      if (result.success) {
        alert('Horarios de operación actualizados');
      } else {
        alert(result.error || 'Error al actualizar');
      }
    } catch (error) {
      alert('Error de conexión');
    }
  };

  const handleSavePaymentSettings = async () => {
    try {
      const response = await fetch('/api/empresa/settings/payment-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('empresa-token')}`
        },
        body: JSON.stringify(paymentSettings)
      });
      const result = await response.json();
      if (result.success) {
        alert('Configuración de pagos actualizada');
      } else {
        alert(result.error || 'Error al actualizar');
      }
    } catch (error) {
      alert('Error de conexión');
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('¿Estás seguro de que deseas eliminar tu cuenta? Esta acción no se puede deshacer.')) {
      if (window.confirm('¿Realmente quieres eliminar tu cuenta permanentemente?')) {
        try {
          const response = await fetch('/api/empresa/account', {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('empresa-token')}`
            }
          });
          const result = await response.json();
          if (result.success) {
            alert('Cuenta eliminada');
            logout();
          } else {
            alert(result.error || 'Error al eliminar cuenta');
          }
        } catch (error) {
          alert('Error de conexión');
        }
      }
    }
  };

  const dayNames = {
    monday: 'Lunes',
    tuesday: 'Martes',
    wednesday: 'Miércoles',
    thursday: 'Jueves',
    friday: 'Viernes',
    saturday: 'Sábado',
    sunday: 'Domingo'
  };

  return (
    <Layout headerType="empresa">
      <SettingsContainer>
        <SettingsHeader>
          <h1>Configuración</h1>
          <p>Gestiona la configuración de tu empresa y cuenta</p>
        </SettingsHeader>

        {/* Información del Negocio */}
        <SettingsCard>
          <SettingsSection>
            <h2>Información del Negocio</h2>

            <LogoRow>
              <LogoPreview>
                {logoUrl ? (
                  <img src={logoUrl} alt={`Logo de ${businessInfo.businessName || 'tu empresa'}`} />
                ) : (
                  <span aria-hidden="true">
                    {(businessInfo.businessName || '?').trim().charAt(0).toUpperCase()}
                  </span>
                )}
              </LogoPreview>
              <LogoActions>
                <LogoButtons>
                  <input
                    ref={logoInput}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    hidden
                    onChange={handleLogoSelected}
                  />
                  <Button
                    type="button"
                    size="small"
                    variant="secondary"
                    disabled={logoBusy}
                    onClick={() => logoInput.current && logoInput.current.click()}
                  >
                    {logoBusy ? 'Procesando…' : logoUrl ? 'Cambiar logo' : 'Subir logo'}
                  </Button>
                  {logoUrl && (
                    <Button
                      type="button"
                      size="small"
                      variant="ghost"
                      disabled={logoBusy}
                      onClick={handleLogoRemove}
                    >
                      Quitar logo
                    </Button>
                  )}
                </LogoButtons>
                <small>JPEG, PNG o WebP. Se muestra en tu perfil público y al compartirlo.</small>
                {logoError && <p role="alert">{logoError}</p>}
              </LogoActions>
            </LogoRow>

            <form onSubmit={handleSaveBusinessInfo}>
              <FormGrid $cols={2}>
                <FormGroup>
                  <FormLabel>Nombre del negocio</FormLabel>
                  <FormInput
                    type="text"
                    name="businessName"
                    value={businessInfo.businessName}
                    onChange={handleBusinessInfoChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Nombre de contacto</FormLabel>
                  <FormInput
                    type="text"
                    name="contactName"
                    value={businessInfo.contactName}
                    onChange={handleBusinessInfoChange}
                  />
                </FormGroup>
              </FormGrid>

              <FormGrid $cols={2}>
                <FormGroup>
                  <FormLabel>Email</FormLabel>
                  <FormInput
                    type="email"
                    name="email"
                    value={businessInfo.email}
                    onChange={handleBusinessInfoChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Teléfono</FormLabel>
                  <FormInput
                    type="tel"
                    name="phone"
                    value={businessInfo.phone}
                    onChange={handleBusinessInfoChange}
                  />
                </FormGroup>
              </FormGrid>

              <FormGroup>
                <FormLabel>Dirección</FormLabel>
                <FormInput
                  type="text"
                  name="address"
                  value={businessInfo.address}
                  onChange={handleBusinessInfoChange}
                />
              </FormGroup>

              <FormGrid $cols={3}>
                <FormGroup>
                  <FormLabel>Ciudad</FormLabel>
                  <FormInput
                    type="text"
                    name="city"
                    value={businessInfo.city}
                    onChange={handleBusinessInfoChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Estado</FormLabel>
                  <FormInput
                    type="text"
                    name="state"
                    value={businessInfo.state}
                    onChange={handleBusinessInfoChange}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Tipo de negocio</FormLabel>
                  <FormSelect
                    name="businessType"
                    value={businessInfo.businessType}
                    onChange={handleBusinessInfoChange}
                  >
                    <option value="">Seleccionar</option>
                    <option value="parque_acuatico">Parque Acuático</option>
                    <option value="museo">Museo</option>
                    <option value="aventura">Aventura</option>
                    <option value="tours">Tours</option>
                    <option value="renta_vehiculos">Renta de Vehículos</option>
                  </FormSelect>
                </FormGroup>
              </FormGrid>

              <FormGroup>
                <FormLabel>Descripción del negocio</FormLabel>
                <FormTextarea
                  name="description"
                  value={businessInfo.description}
                  onChange={handleBusinessInfoChange}
                  rows="3"
                  placeholder="Describe tu negocio, servicios y qué lo hace especial..."
                />
              </FormGroup>

              <Button type="submit">Guardar Información</Button>
            </form>
          </SettingsSection>
        </SettingsCard>

        {/* Horarios de Operación */}
        <SettingsCard>
          <SettingsSection>
            <h2>Horarios de Operación</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
              Configura los horarios en que tus actividades están disponibles
            </p>
            
            {Object.entries(operatingHours).map(([day, hours]) => (
              <HoursRow key={day}>
                <HoursDay>
                  {dayNames[day]}
                </HoursDay>
                
                <SwitchGroup>
                  <Switch>
                    <input
                      type="checkbox"
                      checked={!hours.closed}
                      onChange={(e) => handleOperatingHourChange(day, 'closed', !e.target.checked)}
                    />
                    <span className="slider"></span>
                  </Switch>
                  <span>Abierto</span>
                </SwitchGroup>

                {!hours.closed && (
                  <HoursTimes>
                    <FormInput
                      type="time"
                      value={hours.open}
                      onChange={(e) => handleOperatingHourChange(day, 'open', e.target.value)}
                    />
                    <span style={{ color: 'var(--text-secondary)' }}>a</span>
                    <FormInput
                      type="time"
                      value={hours.close}
                      onChange={(e) => handleOperatingHourChange(day, 'close', e.target.value)}
                    />
                  </HoursTimes>
                )}
              </HoursRow>
            ))}
            
            <Button onClick={handleSaveOperatingHours}>Guardar Horarios</Button>
          </SettingsSection>
        </SettingsCard>

        {/* Notificaciones */}
        <SettingsCard>
          <SettingsSection>
            <h2>Preferencias de Notificaciones</h2>
            
            <SwitchGroup>
              <Switch>
                <input
                  type="checkbox"
                  checked={notifications.emailReservations}
                  onChange={() => handleNotificationChange('emailReservations')}
                />
                <span className="slider"></span>
              </Switch>
              <div>
                <strong>Email para nuevas reservas</strong>
                <p>Recibe un email cada vez que llegue una nueva reserva</p>
              </div>
            </SwitchGroup>

            <SwitchGroup>
              <Switch>
                <input
                  type="checkbox"
                  checked={notifications.emailPayments}
                  onChange={() => handleNotificationChange('emailPayments')}
                />
                <span className="slider"></span>
              </Switch>
              <div>
                <strong>Email para pagos liberados</strong>
                <p>Notificación cuando se liberen pagos de reservas</p>
              </div>
            </SwitchGroup>

            <SwitchGroup>
              <Switch>
                <input
                  type="checkbox"
                  checked={notifications.smsReservations}
                  onChange={() => handleNotificationChange('smsReservations')}
                />
                <span className="slider"></span>
              </Switch>
              <div>
                <strong>SMS para reservas urgentes</strong>
                <p>Recibe SMS para reservas de último momento</p>
              </div>
            </SwitchGroup>

            <SwitchGroup>
              <Switch>
                <input
                  type="checkbox"
                  checked={notifications.pushNotifications}
                  onChange={() => handleNotificationChange('pushNotifications')}
                />
                <span className="slider"></span>
              </Switch>
              <div>
                <strong>Notificaciones push</strong>
                <p>Notificaciones en tiempo real en el navegador</p>
              </div>
            </SwitchGroup>

            <Button onClick={handleSaveNotifications}>Guardar Preferencias</Button>
          </SettingsSection>
        </SettingsCard>

        {/* Configuración de Pagos */}
        <SettingsCard>
          <SettingsSection>
            <h2>Configuración de Pagos</h2>
            
            <FormGroup>
              <FormLabel>Cuenta bancaria</FormLabel>
              <FormInput
                type="text"
                name="bankAccount"
                value={paymentSettings.bankAccount}
                onChange={handlePaymentSettingsChange}
                placeholder="Número de cuenta bancaria"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>CLABE interbancaria</FormLabel>
              <FormInput
                type="text"
                name="clabe"
                value={paymentSettings.clabe}
                onChange={handlePaymentSettingsChange}
                placeholder="CLABE de 18 dígitos"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Comisión de BokerHub (%)</FormLabel>
              <FormInput
                type="number"
                name="commissionRate"
                value={paymentSettings.commissionRate}
                onChange={handlePaymentSettingsChange}
                step="0.1"
                min="0"
                max="15"
                disabled
              />
              <small style={{ color: 'var(--text-secondary)' }}>
                La comisión actual es del {paymentSettings.commissionRate}%
              </small>
            </FormGroup>

            <SwitchGroup>
              <Switch>
                <input
                  type="checkbox"
                  checked={paymentSettings.autoRelease}
                  onChange={(e) => setPaymentSettings({
                    ...paymentSettings,
                    autoRelease: e.target.checked
                  })}
                />
                <span className="slider"></span>
              </Switch>
              <div>
                <strong>Liberación automática de pagos</strong>
                <p>Los pagos se liberan automáticamente después del tiempo especificado</p>
              </div>
            </SwitchGroup>

            {paymentSettings.autoRelease && (
              <FormGroup>
                <FormLabel>Tiempo de liberación automática (horas)</FormLabel>
                <FormInput
                  type="number"
                  name="releaseDelay"
                  value={paymentSettings.releaseDelay}
                  onChange={handlePaymentSettingsChange}
                  min="1"
                  max="168"
                />
              </FormGroup>
            )}

            <Button onClick={handleSavePaymentSettings}>Guardar Configuración</Button>
          </SettingsSection>
        </SettingsCard>

        {/* Zona de Peligro */}
        <DangerZone>
          <SettingsSection>
            <h2>Zona de Peligro</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
              Estas acciones son permanentes y no se pueden deshacer
            </p>
            
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Button variant="danger" onClick={() => alert('Funcionalidad en desarrollo')}>
                🔒 Suspender Cuenta Temporalmente
              </Button>
              <Button variant="danger" onClick={handleDeleteAccount}>
                🗑️ Eliminar Cuenta Permanentemente
              </Button>
            </div>
          </SettingsSection>
        </DangerZone>
      </SettingsContainer>
      {dialog}
    </Layout>
  );
};

export default Settings;
